import type {
  OrderService,
  WorkflowSlaStatus,
  WorkflowStep,
} from "@repo/shared-types/plugins/logistics-workflow";
import type {
  ChecklistItemView,
  OrderServiceDetailView,
} from "./client";
import { suggestNextStepStatus } from "./suggest-next-step-status";

export function mapOrderToDetailView(os: OrderService): OrderServiceDetailView {
  const steps: WorkflowStep[] = os.workflowInstance?.steps ?? [];
  const current =
    steps.find((s: WorkflowStep) => s.id === os.currentWorkflowStepId) ?? null;
  const missingDocuments = (os.checklistItems ?? [])
    .filter((c) => c.isMandatory && c.status === "NOT_UPLOADED")
    .map((c) => ({
      id: c.id,
      label: c.label ?? c.documentTypeId,
      isMandatory: c.isMandatory,
      documentTypeId: c.documentTypeId,
    }));
  const pendingVerifyDocuments = (os.checklistItems ?? [])
    .filter((c) => c.status === "UPLOADED")
    .map((c) => ({
      id: c.id,
      label: c.label ?? c.documentTypeId,
      documentTypeId: c.documentTypeId,
    }));
  const completedSteps = steps
    .filter((s: WorkflowStep) => s.status === "COMPLETED")
    .map((s: WorkflowStep) => ({
      id: s.id,
      name: s.name,
      completedAt: s.completedAt,
    }));
  const dueMs = os.dueDate ? Date.parse(os.dueDate) : NaN;
  const isOverdue =
    Number.isFinite(dueMs) &&
    dueMs < Date.now() &&
    !["COMPLETED", "CLOSED", "CANCELLED", "REJECTED"].includes(os.status);

  const availableActions = os.availableActions ?? [];
  const nextActionHint = os.nextActionHint ?? null;

  let nextAction: OrderServiceDetailView["nextAction"] = null;
  if (availableActions.length > 0) {
    if (availableActions.includes("submit")) {
      nextAction = {
        label: nextActionHint ?? "Submit",
        kind: "submit",
      };
    }
  } else if (os.status === "DRAFT") {
    nextAction = {
      label: nextActionHint ?? "Submit",
      kind: "submit",
    };
  } else if (
    current &&
    current.status !== "COMPLETED" &&
    current.status !== "SKIPPED" &&
    current.status !== "CANCELLED"
  ) {
    const toStatus = suggestNextStepStatus(current.status);
    if (toStatus) {
      nextAction = {
        label: nextActionHint ?? `Advance: ${current.name}`,
        kind: "step_status",
        stepId: current.id,
        toStatus,
      };
    }
  }

  return {
    id: os.id,
    orderServiceNumber: os.orderServiceNumber,
    serviceTypeName: os.serviceTypeName ?? os.serviceTypeId,
    clientOrganizationName:
      os.clientOrganizationName ?? os.clientOrganizationId,
    storeName: os.storeName ?? os.storeId,
    status: os.status,
    currentStepName: current?.name ?? null,
    assigneeName: os.assigneeName ?? current?.assigneeName ?? null,
    dueDate: os.dueDate,
    isOverdue,
    slaStatus: (current?.slaStatus ??
      (isOverdue ? "OVERDUE" : "NORMAL")) as WorkflowSlaStatus | string,
    clientActionRequired: current?.clientActionRequired ?? false,
    progressPercent: os.progressPercent,
    logisticsBranchId: os.logisticsBranchId,
    updatedAt: os.updatedAt,
    version: os.version,
    logisticsBranchName: os.logisticsBranchName ?? os.logisticsBranchId,
    priority: String(os.priority),
    customerReference: os.customerReference,
    shipmentReference: os.shipmentReference,
    remarks: os.remarks,
    shipmentInfo: os.shipmentInfo ?? null,
    warehouse: os.warehouse ?? null,
    billingAccountId: os.billingAccountId ?? null,
    billingReference: os.billingReference ?? null,
    billingScope:
      os.billingScope != null ? String(os.billingScope) : null,
    templateName:
      os.templateName ?? os.workflowInstance?.templateName ?? null,
    createdById: os.createdById,
    createdByName: os.createdByName ?? null,
    clientOrganizationId: os.clientOrganizationId,
    endCustomerId: os.endCustomerId ?? null,
    endCustomerName: os.endCustomerName ?? null,
    workflowInstanceId: os.workflowInstance?.id ?? null,
    checklistItems: (os.checklistItems ?? []).map(
      (c): ChecklistItemView => ({
        id: c.id,
        documentTypeId: c.documentTypeId,
        label: c.label ?? c.documentTypeId,
        isMandatory: c.isMandatory,
        status: c.status,
        remarksText: c.remarksText,
        isRemarksOnly: c.isRemarksOnly,
        currentDocumentId: c.currentDocumentId ?? null,
        currentDocumentFilename: c.currentDocumentFilename ?? null,
      }),
    ),
    missingDocuments,
    pendingVerifyDocuments,
    completedSteps,
    activeStep: current
      ? {
          id: current.id,
          name: current.name,
          assigneeName: current.assigneeName ?? os.assigneeName ?? null,
          clientActionRequired: current.clientActionRequired,
          slaStatus: String(current.slaStatus),
          startedAt: current.startedAt,
        }
      : null,
    nextAction,
    nextActionHint,
    availableActions,
    activity: os.activity ?? [],
    displaySubtitle: os.displaySubtitle ?? null,
    documentCount: os.documentCount ?? 0,
    exceptionCount: os.exceptionCount ?? 0,
    history: (os.history ?? []).map((h) => ({
      id: h.id,
      at: h.at,
      actorName: h.actorName,
      summary: h.summary,
      action: h.action,
      resourceType: h.resourceType,
      resourceId: h.resourceId,
      resourceLabel: h.resourceLabel ?? null,
      before: h.before,
      after: h.after,
      ip: h.ip,
    })),
    steps: steps.map((s: WorkflowStep) => ({
      id: s.id,
      workflowInstanceId: s.workflowInstanceId,
      name: s.name,
      status: s.status,
      sequence: s.sequence,
      assigneeName: s.assigneeName ?? null,
      assigneeTeamName: s.assigneeTeamName ?? null,
      assigneeDepartmentName: s.assigneeDepartmentName ?? null,
      slaStatus: String(s.slaStatus),
      clientActionRequired: s.clientActionRequired,
      clientConsentRequired: s.clientConsentRequired ?? false,
      isRequired: s.isRequired,
      canSkip: s.canSkip,
      canReopen: s.canReopen,
      canRepeat: s.canRepeat,
      startedAt: s.startedAt,
      completedAt: s.completedAt,
      approvalRequired: s.approvalRequired,
      slaExpectedMinutes: s.slaExpectedMinutes ?? null,
      slaWarningMinutes: s.slaWarningMinutes ?? null,
      parallelGroupKey: s.parallelGroupKey ?? null,
      documentTypeRequirements: s.documentTypeRequirements ?? null,
    })),
  };
}
